import type { fabric } from 'fabric';
import type { FabricEditor } from 'modules/fabric-editor';
import { getKindProfile, resolveKind } from './kind-profiles';
import type { CapturedStyle, StyleCategoryId, StyleField } from './types';

/**
 * Read every style field the source kind's profile declares — Fabric props for
 * plain objects, `obj.data` keys for placeholders, the gradient model through
 * `editor.fillGradient` for `'fillGradient'`-storage fields — and group the
 * captured values by category. `undefined` fields are skipped; categories that
 * end up empty are omitted from `values`.
 */
export function captureStyle(source: fabric.Object, editor: FabricEditor): CapturedStyle {
  const kind = resolveKind(source);
  const profile = getKindProfile(source) ?? {};
  const sourceData = source.data as Record<string, unknown> | undefined;

  const values: CapturedStyle['values'] = {};
  for (const [category, fields] of Object.entries(profile) as Array<[StyleCategoryId, StyleField[]]>) {
    const bucket: Record<string, unknown> = {};
    for (const field of fields) {
      let value: unknown;
      if (field.storage === 'fillGradient') {
        value = editor.fillGradient?.get(source);
      } else if (field.storage === 'prop') {
        value = (source as unknown as Record<string, unknown>)[field.key];
        // A gradient/pattern fill is a live Fabric object, not a portable value —
        // the paired `fillGradient` field captures its size-free model
        // instead, so this raw reference is never carried into the clipboard.
        if (field.key === 'fill' && typeof value !== 'string') value = undefined;
      } else {
        value = sourceData?.[field.key];
      }
      if (value !== undefined) bucket[field.key] = value;
    }
    if (Object.keys(bucket).length > 0) values[category] = bucket;
  }

  const placeholderLabel = sourceData?.placeholderLabel;
  return {
    sourceKind: kind,
    sourceLabel: typeof placeholderLabel === 'string' && placeholderLabel ? placeholderLabel : kind,
    values,
  };
}
